import { FEATURE_FLAGS_STORAGE_KEY } from '@onesource/schemas';
import { useCallback, useEffect, useState } from 'react';

export interface FeatureFlag {
  key: string;
  label: string;
  defaultValue: boolean;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export interface FeatureFlagState {
  [key: string]: boolean;
}

const getDefaultFeatureFlagState = (featureFlags: FeatureFlag[]): FeatureFlagState => {
  return featureFlags.reduce((acc, flag) => {
    acc[flag.key] = flag.defaultValue;
    return acc;
  }, {} as FeatureFlagState);
};

const loadFeatureFlagsFromStorage = (featureFlags: FeatureFlag[]): FeatureFlagState => {
  try {
    const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!stored) {
      return getDefaultFeatureFlagState(featureFlags);
    }

    const parsed = JSON.parse(stored);

    if (typeof parsed !== 'object' || parsed === null) {
      console.warn('Invalid feature flags data in localStorage, using defaults');
      return getDefaultFeatureFlagState(featureFlags);
    }

    const defaults = getDefaultFeatureFlagState(featureFlags);
    return { ...defaults, ...parsed };
  } catch (error) {
    console.warn('Failed to load feature flags from localStorage:', error);
    return getDefaultFeatureFlagState(featureFlags);
  }
};

const saveFeatureFlagsToStorage = (flags: FeatureFlagState): void => {
  try {
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags));
  } catch (error) {
    console.error('Failed to save feature flags to localStorage:', error);
  }
};

/**
 * Check if a feature is enabled (standalone function for use outside React)
 * Reads raw localStorage — no config needed.
 * Returns false for unknown feature flags (safe default).
 */
export const isFeatureFlagEnabled = (flagKey: string): boolean => {
  try {
    const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!stored) {
      return false;
    }

    const parsed = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null) {
      return false;
    }

    if (!(flagKey in parsed)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Unknown feature flag: ${flagKey}`);
      }
      return false;
    }

    return Boolean(parsed[flagKey]);
  } catch {
    return false;
  }
};

const toggleFeatureFlag = (flagKey: string, flags: FeatureFlagState): FeatureFlagState => {
  return {
    ...flags,
    [flagKey]: !flags[flagKey],
  };
};

const isValidFeatureFlagKey = (key: string, featureFlags: FeatureFlag[]): boolean => {
  return featureFlags.some(flag => flag.key === key);
};

export interface UseFeatureFlagUtilsResult {
  flags: FeatureFlagState;
  isFeatureEnabled: (flagKey: string) => boolean;
  toggleFeature: (flagKey: string) => void;
  setFeatureFlag: (flagKey: string, value: boolean) => void;
  resetFeatureFlags: () => void;
  resetFeatureFlag: (flagKey: string) => void;
}

export const useFeatureFlagUtils = (featureFlags: FeatureFlag[]): UseFeatureFlagUtilsResult => {
  const [flags, setFlags] = useState<FeatureFlagState>(() => loadFeatureFlagsFromStorage(featureFlags));

  useEffect(() => {
    saveFeatureFlagsToStorage(flags);
  }, [flags]);

  const checkFeatureEnabled = useCallback((flagKey: string): boolean => {
    return isFeatureFlagEnabled(flagKey);
  }, []);

  const toggleFeature = useCallback(
    (flagKey: string) => {
      if (!isValidFeatureFlagKey(flagKey, featureFlags)) {
        console.warn(`Invalid feature flag key: ${flagKey}`);
        return;
      }

      setFlags(currentFlags => toggleFeatureFlag(flagKey, currentFlags));
    },
    [featureFlags],
  );

  const setFeatureFlag = useCallback(
    (flagKey: string, value: boolean) => {
      if (!isValidFeatureFlagKey(flagKey, featureFlags)) {
        console.warn(`Invalid feature flag key: ${flagKey}`);
        return;
      }

      setFlags(currentFlags => ({
        ...currentFlags,
        [flagKey]: value,
      }));
    },
    [featureFlags],
  );

  const resetFeatureFlags = useCallback(() => {
    const defaults = getDefaultFeatureFlagState(featureFlags);
    setFlags(defaults);
  }, [featureFlags]);

  const resetFeatureFlag = useCallback(
    (flagKey: string) => {
      if (!isValidFeatureFlagKey(flagKey, featureFlags)) {
        console.warn(`Invalid feature flag key: ${flagKey}`);
        return;
      }

      const defaultFlag = featureFlags.find(flag => flag.key === flagKey);
      if (defaultFlag) {
        setFlags(currentFlags => ({
          ...currentFlags,
          [flagKey]: defaultFlag.defaultValue,
        }));
      }
    },
    [featureFlags],
  );

  return {
    flags,
    isFeatureEnabled: checkFeatureEnabled,
    toggleFeature,
    setFeatureFlag,
    resetFeatureFlags,
    resetFeatureFlag,
  };
};
